import { WEEKDAYS } from "@/pages/tina/groups/lib/weekdays";
import type { TinaSiteGroup } from "@/types/tina";

/** One weekday column in the calendar header/grid. */
export interface DayColumn {
  value: number; // ISO 1..7 (Mon..Sun)
  short: string;
  /** True when at least one group runs on this weekday. */
  active: boolean;
}

/** A single day covered by a group's bar, from its first to its last run day. */
export interface GroupBarSegment {
  /** True on a run day; false on an off-day gap inside a non-contiguous span. */
  active: boolean;
}

/** A group rendered as one row: a bar spanning the days it runs. */
export interface GroupRow {
  kind: "group";
  group: TinaSiteGroup;
  /** CSS grid-column for the bar within the row grid ("<col> / span <len>"). */
  gridColumn: string;
  /** One segment per covered day, first → last run day inclusive. */
  segments: GroupBarSegment[];
  /** "HH:00" gutter label — shown only on the first group of an hour bucket, "" otherwise. */
  hourLabel: string;
  /** ":MM" shown inside the bar when the group runs off the hour; null on the hour. */
  minuteLabel: string | null;
}

/** A slim filler row standing in for one or more consecutive hours with no groups. */
export interface GapRow {
  kind: "gap";
  /** Empty-hour range label — "13:00" for a single hour, "13:00 – 20:59" for a span. */
  label: string;
}

export type CalendarRow = GroupRow | GapRow;

export interface WeeklyLayout {
  /** Mon..Sun columns with per-day active flags — drives column collapse. */
  columns: DayColumn[];
  /** grid-template-columns: 66px time gutter + one track per weekday. */
  gridTemplateColumns: string;
  /** Interleaved group rows (hour-bucketed) and gap rows, top to bottom. */
  rows: CalendarRow[];
}

/** Grid placement + per-day segments for one group's spanning bar. */
function barPlacement(
  group: TinaSiteGroup,
): Pick<GroupRow, "gridColumn" | "segments"> {
  const runsOn = WEEKDAYS.map((w) => group.days_of_week.includes(w.value));
  const first = runsOn.indexOf(true);
  const last = runsOn.lastIndexOf(true);
  if (first === -1) {
    // No scheduled days — shouldn't occur, but keep a stable full-width row.
    return { gridColumn: "2 / -1", segments: [] };
  }
  return {
    gridColumn: `${first + 2} / span ${last - first + 1}`,
    segments: runsOn.slice(first, last + 1).map((active) => ({ active })),
  };
}

/**
 * Derive the hour-bucketed weekly-calendar layout from the group list.
 *
 * Groups are sorted by time_of_day (tie-broken by name) and bucketed by hour
 * from the earliest to the latest group's hour. Each bucket's gutter shows its
 * hour once (on the first group); off-the-hour minutes surface inside the bar
 * instead. Runs of empty hours collapse into a single slim gap row. Each group
 * still renders as one bar spanning the days it runs; weekdays with no groups
 * collapse to a narrow track. The time gutter is grid column 1, so weekday
 * ISO d sits at grid column d + 1.
 */
export function buildWeeklyLayout(groups: TinaSiteGroup[]): WeeklyLayout {
  const activeDays = new Set<number>();
  for (const group of groups) {
    for (const day of group.days_of_week) activeDays.add(day);
  }

  const columns: DayColumn[] = WEEKDAYS.map((w) => ({
    value: w.value,
    short: w.short,
    active: activeDays.has(w.value),
  }));

  const gridTemplateColumns = `66px ${columns
    .map((c) => (c.active ? "1fr" : "0.35fr"))
    .join(" ")}`;

  const sorted = [...groups].sort(
    (a, b) =>
      a.time_of_day.localeCompare(b.time_of_day) ||
      a.name.localeCompare(b.name),
  );

  const rows: CalendarRow[] = [];
  if (sorted.length > 0) {
    const pad = (h: number) => String(h).padStart(2, "0");
    const hourOf = (g: TinaSiteGroup) =>
      parseInt(g.time_of_day.slice(0, 2), 10);
    const firstH = hourOf(sorted[0]);
    const lastH = hourOf(sorted[sorted.length - 1]);
    let emptyStart: number | null = null;

    for (let h = firstH; h <= lastH; h++) {
      const inHour = sorted.filter((g) => hourOf(g) === h);
      if (inHour.length === 0) {
        if (emptyStart === null) emptyStart = h;
        continue;
      }
      if (emptyStart !== null) {
        rows.push({
          kind: "gap",
          label:
            emptyStart === h - 1
              ? `${pad(emptyStart)}:00`
              : `${pad(emptyStart)}:00 – ${pad(h - 1)}:59`,
        });
        emptyStart = null;
      }
      inHour.forEach((group, j) => {
        const minutes = group.time_of_day.slice(3, 5);
        rows.push({
          kind: "group",
          group,
          ...barPlacement(group),
          hourLabel: j === 0 ? `${pad(h)}:00` : "",
          minuteLabel: minutes === "00" ? null : `:${minutes}`,
        });
      });
    }
  }

  return { columns, gridTemplateColumns, rows };
}
